import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { notesApi, type ApiListResponse } from '@/lib/api'
import { useNotificationStore } from './notifications'

export interface Note {
  _id: string
  body: string
  mentions?: string[]
  contactId?: string
  dealId?: string
  companyId?: string
  orgId: string
  createdBy: string
  updatedBy: string
  author?: {
    _id: string
    name: string
    email: string
  }
  mentionedUsers?: {
    _id: string
    name: string
    email: string
  }[]
  contact?: {
    _id: string
    firstName: string
    lastName?: string
  }
  deal?: {
    _id: string
    name: string
  }
  company?: {
    _id: string
    name: string
  }
  createdAt: string
  updatedAt: string
  attachments?: { _id: string; originalName: string; mime: string; size: number }[]
}

export interface NotesState {
  notes: Note[]
  loading: boolean
  error: string | null
  meta: {
    page: number
    pageSize: number
    total: number
    hasNext: boolean
  } | null
}

export const useNotesStore = defineStore('notes', () => {
  const notes = ref<Note[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const meta = ref<NotesState['meta']>(null)

  const notifications = useNotificationStore()

  // Computed
  const notesCount = computed(() => notes.value.length)

  const notesByEntity = computed(() => {
    const grouped: Record<string, Note[]> = {}
    notes.value.forEach(note => {
      const key = note.contactId || note.dealId || note.companyId || 'unknown'
      if (!grouped[key]) {
        grouped[key] = []
      }
      grouped[key].push(note)
    })
    return grouped
  })

  // Actions
  async function fetchNotes(params?: any): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response: ApiListResponse<Note> = await notesApi.list(params)
      notes.value = response.data
      meta.value = response.meta
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch notes'
      notifications.error('Failed to load notes')
    } finally {
      loading.value = false
    }
  }

  async function fetchContactNotes(contactId: string, params?: any): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response: ApiListResponse<Note> = await notesApi.getByContact(contactId, params)
      // Update or add notes to the store
      const contactNotes = response.data

      // Remove existing notes for this contact and add new ones
      notes.value = notes.value.filter(note => note.contactId !== contactId)
      notes.value.push(...contactNotes)

      // Sort by creation date (newest first)
      notes.value.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch contact notes'
      notifications.error('Failed to load notes')
    } finally {
      loading.value = false
    }
  }

  async function createNote(noteData: Partial<Note>): Promise<Note | null> {
    loading.value = true
    error.value = null

    try {
      console.log('Notes store: Creating note with data:', noteData)
      const response = await notesApi.create(noteData)
      const newNote = response.data

      // Add to beginning of array (newest first)
      notes.value.unshift(newNote)

      notifications.success('Note added successfully')
      return newNote
    } catch (err: any) {
      console.error('Notes store: Create note error:', err)
      error.value = err.response?.data?.error?.message || 'Failed to create note'
      notifications.error('Failed to add note')
      return null
    } finally {
      loading.value = false
    }
  }

  async function updateNote(id: string, noteData: Partial<Note>): Promise<Note | null> {
    loading.value = true
    error.value = null

    try {
      const response = await notesApi.update(id, noteData)
      const updatedNote = response.data

      const index = notes.value.findIndex(n => n._id === id)
      if (index !== -1) {
        notes.value[index] = updatedNote
      }

      notifications.success('Note updated successfully')
      return updatedNote
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to update note'
      notifications.error('Failed to update note')
      return null
    } finally {
      loading.value = false
    }
  }

  async function deleteNote(id: string): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      await notesApi.delete(id)
      notes.value = notes.value.filter(n => n._id !== id)
      notifications.success('Note deleted successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to delete note'
      notifications.error('Failed to delete note')
      return false
    } finally {
      loading.value = false
    }
  }

  function getContactNotes(contactId: string): Note[] {
    return notes.value.filter(note => note.contactId === contactId)
  }

  function getDealNotes(dealId: string): Note[] {
    return notes.value.filter(note => note.dealId === dealId)
  }

  function getCompanyNotes(companyId: string): Note[] {
    return notes.value.filter(note => note.companyId === companyId)
  }

  function getNoteById(id: string): Note | undefined {
    return notes.value.find(n => n._id === id)
  }

  function clearNotes(): void {
    notes.value = []
    meta.value = null
    error.value = null
  }

  return {
    // State
    notes,
    loading,
    error,
    meta,

    // Computed
    notesCount,
    notesByEntity,

    // Actions
    fetchNotes,
    fetchContactNotes,
    createNote,
    updateNote,
    deleteNote,
    getContactNotes,
    getDealNotes,
    getCompanyNotes,
    getNoteById,
    clearNotes,
  }
})
